import { EMOJI_CATEGORIES } from "./emoji/categories"
import { getEmojiForShortcodeName } from "./emoji/search"

/** The automatic ✅ on a thread's first customer message when its linked issues are done. */
export const COMPLETED_REACTION_EMOJI = "white_check_mark"

/** Reactor for reactions Productlane adds on its own, like the completion ✅. */
export const AUTOMATIC_REACTOR_KEY = "productlane"

export function memberReactorKey(userId: string): string {
	return `user:${userId}`
}

export function slackReactorKey(slackUserId: string): string {
	return `slack:${slackUserId}`
}

export function linearReactorKey(linearUserId: string): string {
	return `linear:${linearUserId}`
}

/** The table a reaction's messageId points at. */
export type MessageReactionType = "slack" | "comment"

/** Linear names emoji like Slack but has no skin tones, so a toned reaction syncs as its base emoji. */
export function linearReactionEmoji(name: string): string {
	return name.split("::")[0] || name
}

const SKIN_TONES: Record<string, string> = {
	"skin-tone-2": "\u{1F3FB}",
	"skin-tone-3": "\u{1F3FC}",
	"skin-tone-4": "\u{1F3FD}",
	"skin-tone-5": "\u{1F3FE}",
	"skin-tone-6": "\u{1F3FF}",
}

let slackGlyphs: Map<string, string> | null = null

// Slack's own names, for emoji newer than the shortcode map.
function slackGlyph(name: string): string | undefined {
	slackGlyphs ??= new Map(
		EMOJI_CATEGORIES.flatMap((category) =>
			category.emojis.flatMap(([base, glyph, ...aliases]) =>
				[base, ...aliases].map((alias) => [alias, glyph] as const),
			),
		),
	)
	return slackGlyphs.get(name)
}

/** Slack's reaction name ("+1::skin-tone-3") as a glyph; undefined for custom or unknown emoji. */
export function reactionGlyph(name: string): string | undefined {
	const [base, tone] = name.split("::")
	const glyph = base
		? (getEmojiForShortcodeName(base) ?? slackGlyph(base))
		: undefined
	if (!glyph) return undefined
	const modifier = tone ? SKIN_TONES[tone] : undefined
	return modifier ? `${glyph}${modifier}` : glyph
}

/** How a reaction reads in plain text, e.g. in a notification title. */
export function reactionText(name: string): string {
	return reactionGlyph(name) ?? `:${name}:`
}

export interface ReactionRow {
	id: string
	messageId: string
	emoji: string
	imageUrl?: string | null
	reactorKey: string
	reactorName?: string | null
	userId?: string | null
	createdAt: number
}

export interface ReactionGroup {
	emoji: string
	imageUrl: string | null
	count: number
	reactors: ReactionRow[]
	/** The member reacted from Productlane, so clicking the chip takes it back. */
	reactedByMe: boolean
}

/** One chip per emoji on each message, in the order each emoji first appeared. */
export function groupReactionsByMessage(
	rows: readonly ReactionRow[],
	currentUserId: string | null | undefined,
): Map<string, ReactionGroup[]> {
	const mine = currentUserId ? memberReactorKey(currentUserId) : null
	const byMessage = new Map<string, Map<string, ReactionGroup>>()
	const sorted = [...rows].sort(
		(a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id),
	)
	for (const row of sorted) {
		let groups = byMessage.get(row.messageId)
		if (!groups) {
			groups = new Map()
			byMessage.set(row.messageId, groups)
		}
		const group = groups.get(row.emoji) ?? {
			emoji: row.emoji,
			imageUrl: null,
			count: 0,
			reactors: [],
			reactedByMe: false,
		}
		group.count += 1
		group.reactors.push(row)
		group.imageUrl ??= row.imageUrl ?? null
		if (row.reactorKey === mine) group.reactedByMe = true
		groups.set(row.emoji, group)
	}
	return new Map(
		[...byMessage].map(([messageId, groups]) => [
			messageId,
			[...groups.values()],
		]),
	)
}
